/**
 * What a screener section was last looking at.
 *
 * ## Why this is not workspace data (yet)
 *
 * The obvious home is `ScreenerConfig.group` — it already exists, it already
 * persists, and it already means "the universe". It is deliberately not used
 * here, because that field currently names a **finset directory** and the
 * screener resolves it against snapshots an upstream job materialises. Writing
 * a symbol-group code into it would change the meaning of a saved field in
 * place: every existing workspace already says `group: "SP500"`, and restoring
 * *that* as a symbol-group selection would silently swap a user's JASDOTS
 * columns for OHLC ones on next load. A migration should be a decision, not a
 * side effect of shipping a picker.
 *
 * So the selection lives here, keyed by section id. It is genuinely view state
 * — the same kind of thing as which tab is open — and it survives a reload,
 * which is what "remember what I was looking at" asks for.
 *
 * **When the contract gains a proper field** (`ScreenerConfig.symbolGroup`, or
 * `group` once finset is retired), this module should be deleted and the value
 * moved into the workspace, where it will sync across devices as it should.
 * That is the intended end state; this is the step that does not break anyone
 * on the way there.
 *
 * ## Two views, one record
 *
 * The bar has a Single Select mode and a Multi Select mode, and each keeps its
 * own answer: switching to Multi Select and back must not lose the group you
 * were on, and switching away from a six-group mix must not throw the mix away.
 * So `group` and `picked` both persist, and `view` says which one is live.
 *
 * Everything here is failure-tolerant: Safari private mode throws on
 * `localStorage` access, and a symbol picker is not worth a blank app. It is
 * also **shape-tolerant**, because the stored record predates `view`, `picked`
 * and `combine` — a browser holding the old two-field form must keep working,
 * which is what `normalise` is for.
 */

const KEY = "qufolio.screener.group";

/** Marks a record saved since Single became everyone's default; see `normalise`. */
const STAMP = "singleSince";
const SINGLE_SINCE = "2026-10-31";

/** Which mode the bar is in. */
export type GroupView = "single" | "multi";

/** How several groups combine. `or` is the union, `and` the intersection. */
export type CombineMode = "or" | "and";

/**
 * One entry in the Multi Select working set.
 *
 * `on` is what lets a chip be switched off from the bar without being removed:
 * chosen but not in the query. Deleting it instead would make the only way back
 * a trip through the type menu to find it again.
 */
export interface PickedGroup {
  groupType: string;
  code: string;
  on: boolean;
}

export interface GroupSelection {
  view: GroupView;
  /**
   * Which markets the bar is showing, as ISO codes. Empty means **all of them**
   * rather than none — a browser that predates this field holds no value, and
   * the honest reading of "unspecified" is that nothing was excluded.
   *
   * A multi-select rather than a switch, because the picker's lists mostly do
   * not collide: standard universes and every theme are uniquely named across
   * markets. Only five names appear twice, and those merge — see
   * `mergeKey`.
   */
  markets: string[];
  /**
   * Whether this browser has ever been given an opening selection.
   *
   * Explicit, because the alternative was an **inference that depended on the
   * default**: "Single Select with no group is not a place the bar can be left"
   * was true, and stopped being true the moment Multi Select became the default,
   * since an empty working set is reachable by removing chips. A new device then
   * opened with nothing selected and so screened nothing — the exact failure
   * `seedSelection` exists to prevent.
   *
   * Absent in stored records, which reads as false and re-seeds once. That is
   * harmless: seeding only fills an empty selection.
   */
  seeded: boolean;
  /** Single Select: which type's chips are shown. */
  groupType: string;
  /** Single Select: the chosen group, or null for none. */
  group: string | null;
  /** Multi Select: the working set, in the order it was built. */
  picked: PickedGroup[];
  combine: CombineMode;
}

/**
 * The group a browser with no selection opens on.
 *
 * A code rather than a position in the catalog, so what a new device shows does
 * not move when the server's table order does.
 */
const OPENING_GROUPS = ["SP500", "IN_NIFTY_100"] as const;

export const DEFAULT_SELECTION: GroupSelection = {
  // Single Select is the default (owner, 2026-10-31): a new member opens on one
  // group, SP500, and reaches for Multiple when they want a combination.
  view: "single",
  markets: [],
  seeded: false,
  groupType: "standard",
  group: null,
  picked: [],
  combine: "or",
};

/**
 * Coerce whatever is in storage into a complete selection.
 *
 * Field by field rather than a shape check, because the failure this guards
 * against is not a corrupt record but an **older** one: `{groupType, group}` is
 * what every browser that used the bar before Multi Select existed is holding,
 * and it is perfectly good data for the half it describes.
 */
function normalise(raw: unknown): GroupSelection | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const value = raw as Partial<GroupSelection>;

  const picked = Array.isArray(value.picked)
    ? value.picked
        .filter(
          (p): p is PickedGroup =>
            !!p && typeof p.groupType === "string" && typeof p.code === "string",
        )
        // `on` absent means on: a record written before a chip could be
        // switched off describes a set that is entirely in the query.
        .map((p) => ({ groupType: p.groupType, code: p.code, on: p.on !== false }))
    : [];

  // **Every record written before Single became the default opens on Single
  // once** (owner, 2026-10-31). Its Single group is the one it named, else
  // Multiple's first chip that is on; with neither it re-seeds. The next save
  // stamps it, and from then on the user's mode is theirs.
  const stamped = (raw as Stored)[STAMP] === SINGLE_SINCE;
  const firstOn = picked.find((p) => p.on);
  const group =
    typeof value.group === "string" ? value.group : stamped ? null : (firstOn?.code ?? null);
  const groupType =
    typeof value.group === "string" || stamped || !firstOn ? value.groupType : firstOn.groupType;

  return {
    // Only a stored "multi" is Multiple. A record with **no view at all**
    // predates Multi Select and was a single-select choice; anything else
    // unrecognised takes the default, which is Single.
    view: stamped && value.view === "multi" ? "multi" : "single",
    // Absent means "all markets", not "no markets" — see the field's own note.
    markets: Array.isArray(value.markets)
      ? value.markets.filter((m): m is string => typeof m === "string")
      : [],
    // Moved to Single with no group to show: seed it, or it screens nothing.
    seeded: value.seeded === true && (stamped || group !== null),
    groupType: typeof groupType === "string" && groupType ? groupType : DEFAULT_SELECTION.groupType,
    group,
    picked,
    combine: value.combine === "and" ? "and" : "or",
  };
}

type Stored = Record<string, unknown>;

function readAll(): Stored {
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Stored) : {};
  } catch {
    return {};
  }
}

/** The selection for one section, or undefined if it has none. */
export function recallSelection(sectionId: string): GroupSelection | undefined {
  return normalise(readAll()[sectionId]);
}

/**
 * Remember one section's selection.
 *
 * Keyed by section id rather than stored globally, because two screener
 * sections in one layout are two independent views — sharing one key would make
 * choosing a group in one silently retarget the other on the next load.
 */
export function rememberSelection(sectionId: string, selection: GroupSelection): void {
  try {
    const all = readAll();
    all[sectionId] = { ...selection, [STAMP]: SINGLE_SINCE };
    window.localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    // Storage unavailable or full — the app works, it just forgets.
  }
}

/**
 * What makes two groups **the same entry** in the picker across markets.
 *
 * Type plus name, because that is exactly what collides and nothing else does.
 * Measured on the live catalog: of 81 active groups, five names appear in two
 * markets — Large / Mid / Small Cap, All Symbols, ETFs — and every theme,
 * every index and every exchange rule is uniquely named.
 *
 * So one "Large Cap" chip selects `LARGE` **and** `IN_LARGE` when both markets
 * are on, which is the right reading: each market's own notion of large. The US
 * band is a dollar threshold and India's is an AMFI rank, and those are not
 * convertible — but "large in its own market" is, and it is what a person means
 * by the phrase. `MEGA` and `MICRO` stay US-only because SEBI has no such tier,
 * which falls out of the same rule rather than needing a special case.
 */
export function mergeKey(groupType: string, name: string): string {
  return `${groupType} ${name.trim().toLowerCase()}`;
}

/** The codes a selection asks the server for, in order. Empty ⇒ no request. */
export function selectedCodes(selection: GroupSelection): string[] {
  if (selection.view === "single") return selection.group ? [selection.group] : [];
  return selection.picked.filter((p) => p.on).map((p) => p.code);
}

/**
 * What a browser that has never chosen should open on, or `null` to leave it.
 *
 * **With no group the screener shows no rows** — that is now the whole of it,
 * and it is why this exists. Because the selection lives in `localStorage`,
 * "never chosen" is the state of every *new device*: a phone opening the app
 * for the first time would land on "No group picked" while the desktop beside
 * it worked from a choice it had remembered months earlier.
 *
 * It used to be worse rather than emptier — with nothing picked the view fell
 * through to a finset socket named by `ScreenerConfig.group`/`screen`, a pair
 * saved with the workspace long ago and on at least one machine pointing at
 * `SP500/JASDOTS`, a screen that no longer exists. That fallback is gone. The
 * hole it papered over is the one this fills: the first answer given to
 * somebody who has never chosen is a real symbol group.
 *
 * ## It reads the selection, never storage
 *
 * "Has this browser chosen before" cannot be answered by asking storage,
 * because the view persists its selection on mount — so by the time the
 * catalog arrives, storage always says yes, holding the default it just wrote
 * itself. A device that had opened the app once was therefore indistinguishable
 * from one that had used it for a year, and the first version of this fix
 * helped only browsers that had literally never loaded the page.
 *
 * A **`seeded` flag** answers it directly. This used to be inferred — "Single
 * Select with no group is not a place the bar can be left" — which was true and
 * stopped being true when Multi Select became the default, because an empty
 * working set *is* reachable by removing chips. The inference silently became
 * "never seed anything", and a new device opened on nothing again.
 *
 * So it seeds once, whichever view, and never again. After that an empty
 * selection belongs to the user and refilling it would make the bar impossible
 * to clear.
 */
export function seedSelection(
  groups: readonly { code: string; groupType: string }[],
  current: GroupSelection,
  preferredType: string = DEFAULT_SELECTION.groupType,
): GroupSelection | null {
  // Asked and answered once. After that an empty selection is the user's, and
  // refilling it would make the bar impossible to clear.
  if (current.seeded) return null;
  // Already on something — nothing to fill in.
  if (selectedCodes(current).length > 0) {
    return { ...current, seeded: true };
  }
  // **A named opening group, not whichever one happens to be first.** The rule
  // was "the first of the preferred type", which is the server's table order —
  // so what a new browser opened on was decided by however the group catalog
  // happened to be sorted that day, and it changed when the catalog did. SP500
  // is the one universe every reader recognises and the one the demo ships, so
  // a new device and the demo now open on the same thing.
  //
  // **One name per market, tried in order**, because "SP500 is not guaranteed
  // to exist" stopped being hypothetical: an account entitled to India alone is
  // handed a catalog with no SP500 in it, and falling through to `groups[0]`
  // opened it on whatever the table order put first. `IN_NIFTY_100` is India's
  // equivalent — the index every reader there recognises — so each market opens
  // on its own familiar universe rather than on an accident.
  //
  // The old rule stays as the fallback rather than being replaced, because
  // opening on nothing is the exact failure this function exists to prevent.
  //
  // **The preferred type still outranks the name**, and the order matters: a
  // caller asking for `theme` means it, so an untyped SP500 lookup placed above
  // this would answer SP500 to every question and quietly ignore the argument.
  const first =
    OPENING_GROUPS.map((code) =>
      groups.find((g) => g.groupType === preferredType && g.code === code),
    ).find(Boolean) ??
    groups.find((g) => g.groupType === preferredType) ??
    OPENING_GROUPS.map((code) => groups.find((g) => g.code === code)).find(Boolean) ??
    groups[0];
  if (!first) return null;
  return current.view === "single"
    ? { ...current, groupType: first.groupType, group: first.code, seeded: true }
    : {
        ...current,
        groupType: first.groupType,
        picked: [{ groupType: first.groupType, code: first.code, on: true }],
        seeded: true,
      };
}
